import type {
  FindTasksInput,
  Task,
  TaskFlag,
  TaskPriority,
  TaskProfile,
} from '@travel-planner/shared/schemas/tasks';
import { useAuthData } from '@auth/features/auth';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  coerceFlag,
  TaskPriorityCell,
  useListTasks,
} from '@task-manager/entities/tasks';
import { ConfirmDeleteDialog } from '@task-manager/features/task-delete';
import {
  TaskCreateModal,
  TaskUpdateModal,
} from '@task-manager/features/task-form';
import { ApplyCollectionDialog } from '@task-manager/features/task-from-set';
import {
  TaskCompleteButton,
  TaskFlagToggle,
  TaskMoreMenu,
} from '@task-manager/features/task-row-actions';
import { TaskSnoozeModal } from '@task-manager/features/task-snooze';
import {
  mergeSearch,
  TasksFiltersPopover,
} from '@task-manager/features/tasks-filters';
import { SORT_ORDER } from '@travel-planner/shared/types';
import { format, parseISO } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Plus,
  Search,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { useDebouncedValue } from '@/shared/lib/use-debounced-value';
import {
  Button,
  Input,
  Pagination,
  PaginationButton,
  Select,
  SortableHeader,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  TableSkeletonRow,
  Text,
  Tooltip,
} from '@/shared/ui';
import { getExpireOnDisplay } from '../lib/expire-on-label';
import css from './tasks-table.module.scss';

const PAGE_SIZE_OPTIONS = [
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '30', value: '30' },
  { label: '40', value: '40' },
  { label: '50', value: '50' },
];
const SKELETON_ROWS = 8;

type SortField =
  | 'title'
  | 'priority'
  | 'profile'
  | 'itinerary'
  | 'collection'
  | 'expireOn'
  | 'completedOn'
  | 'createdOn';

const SORT_FIELDS: ReadonlyArray<SortField> = [
  'title',
  'priority',
  'profile',
  'itinerary',
  'collection',
  'expireOn',
  'completedOn',
  'createdOn',
];

const isSortField = (v: unknown): v is SortField =>
  typeof v === 'string' && (SORT_FIELDS as ReadonlyArray<string>).includes(v);

const COLUMNS: Array<{
  id: string;
  label: string;
  align: 'left' | 'center' | 'right';
  width?: string;
  sortField?: SortField;
}> = [
  { id: 'title', label: 'Title', align: 'left', sortField: 'title' },
  {
    id: 'priority',
    label: 'Priority',
    align: 'center',
    width: '110px',
    sortField: 'priority',
  },
  {
    id: 'profile',
    label: 'Profile',
    align: 'center',
    width: '100px',
    sortField: 'profile',
  },
  {
    id: 'itinerary',
    label: 'Itinerary',
    align: 'left',
    width: '130px',
    sortField: 'itinerary',
  },
  {
    id: 'collection',
    label: 'Collection',
    align: 'left',
    width: '140px',
    sortField: 'collection',
  },
  {
    id: 'expireOn',
    label: 'Expire On',
    align: 'left',
    width: '180px',
    sortField: 'expireOn',
  },
  {
    id: 'completedOn',
    label: 'Completed On',
    align: 'left',
    width: '180px',
    sortField: 'completedOn',
  },
  { id: 'actions', label: 'Actions', align: 'right', width: '180px' },
];

const styles = pickCss(css, css.root);

const capitalize = (s: string | null | undefined): string => {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// "Thursday, October 23" — legacy day-of-week + month + day format.
const formatLongDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'EEEE, MMMM d');
  } catch {
    return '—';
  }
};

const expireVariant = (
  iso: string | null | undefined
): 'default' | 'soon' | 'past' => {
  if (!iso) return 'default';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'default';
  const diffMs = d.getTime() - Date.now();
  if (diffMs < 0) return 'past';
  if (diffMs / (1000 * 60 * 60 * 24) < 3) return 'soon';
  return 'default';
};

const SKELETON_COLUMNS = [
  { width: '70%' },
  { width: '50%' },
  { width: '50%' },
  { width: '50%' },
  { width: '50%' },
  { width: '50%' },
  { width: '50%' },
  { width: '50%' },
];

type TasksTableProps = {
  /**
   * Server-side filters applied on top of the URL filters. Used by the
   * drawer to pre-filter to a specific itinerary or account without
   * touching the URL. When set, the toolbar still works but the scope
   * is locked.
   */
  scope?: {
    itineraryId?: string;
    assignedAccountId?: string;
  };
  /**
   * `'admin'` renders the legacy admin Tasks view: the **template** task
   * library (`unassignedOnly` — no assignee), with manager-only columns
   * and actions trimmed (no Itinerary / Completed On / flag+complete /
   * Add-From-Collection / assignee filter). `'default'` (page + drawer)
   * keeps the assigned-tasks behaviour.
   */
  variant?: 'default' | 'admin';
};

export const TasksTable = ({ scope, variant = 'default' }: TasksTableProps) => {
  const isAdmin = variant === 'admin';
  // Admin (template) mode drops the assignee/itinerary/completion-centric
  // columns — templates have none of those. Legacy admin Tasks view.
  const columns = isAdmin
    ? COLUMNS.filter((c) => c.id !== 'itinerary' && c.id !== 'completedOn')
    : COLUMNS;
  const navigate = useNavigate();
  const { data: authData } = useAuthData();
  const currentUserId = authData?.user.id;
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  const urlTitle = typeof search.title === 'string' ? search.title : '';
  const urlPriorities = Array.isArray(search.priorities)
    ? (search.priorities.filter(
        (p) => p === 'high' || p === 'medium' || p === 'low'
      ) as TaskPriority[])
    : undefined;
  const urlProfiles = Array.isArray(search.profiles)
    ? (search.profiles.filter(
        (p) => p === 'consultant' || p === 'finance' || p === 'operation'
      ) as TaskProfile[])
    : undefined;
  const urlFlags = Array.isArray(search.flags)
    ? (search.flags.filter((f) => f === 0 || f === 5 || f === 10) as TaskFlag[])
    : undefined;
  const urlItineraryIds = Array.isArray(search.itineraryIds)
    ? (search.itineraryIds.filter(
        (id): id is string => typeof id === 'string'
      ) as string[])
    : undefined;
  const urlCollectionIds = Array.isArray(search.collectionIds)
    ? (search.collectionIds.filter(
        (id): id is string => typeof id === 'string'
      ) as string[])
    : undefined;
  // Assignee filter (full page only — the drawer locks it via `scope`).
  // URL semantics: absent → default to the current user ("My tasks");
  // 'all' → everyone; any other string → that account id. This lets the
  // page open on the user's own tasks while still allowing them to widen
  // the view or pick a teammate.
  const urlAssignee =
    typeof search.assignedAccountId === 'string'
      ? search.assignedAccountId
      : undefined;
  const effectiveAssignee = scope
    ? scope.assignedAccountId
    : urlAssignee === 'all'
      ? undefined
      : (urlAssignee ?? currentUserId);
  // What the assignee <Select> should show: the explicit URL choice, or
  // the current user when defaulting, or 'all' once the user widens it.
  const assigneeSelectValue = urlAssignee ?? currentUserId ?? 'all';
  const sortBy = isSortField(search.sortBy) ? search.sortBy : undefined;
  const sortOrder =
    search.sortOrder === 'asc' || search.sortOrder === 'desc'
      ? search.sortOrder
      : undefined;
  const page =
    typeof search.page === 'number' && search.page > 0 ? search.page : 1;

  const [searchInput, setSearchInput] = useState(urlTitle);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const lastWrittenSearchRef = useRef<string | undefined>(urlTitle);

  const updateSearch = useCallback(
    (updates: Record<string, unknown>) => {
      // biome-ignore lint/suspicious/noExplicitAny: TanStack Router's strict `to`/`search` types require a registered route; this widget is reused inside the drawer outside of /tasks-management so we go untyped here.
      const opts: any = {
        to: '.',
        search: (prev: Record<string, unknown>) => mergeSearch(prev, updates),
        replace: true,
      };
      navigate(opts);
    },
    [navigate]
  );

  // Single bidirectional sync between the search input and the URL —
  // mirrors the products-table pattern (products-table.tsx:197-209).
  useEffect(() => {
    const current = urlTitle || undefined;
    const desired = debouncedSearch || undefined;
    if (current !== lastWrittenSearchRef.current) {
      lastWrittenSearchRef.current = current;
      setSearchInput(current ?? '');
      return;
    }
    if (desired !== current) {
      lastWrittenSearchRef.current = desired;
      updateSearch({ title: desired, page: undefined });
    }
  }, [debouncedSearch, urlTitle, updateSearch]);

  const handleSortToggle = useCallback(
    (field: SortField) => {
      if (sortBy !== field) {
        updateSearch({ sortBy: field, sortOrder: 'desc', page: undefined });
        return;
      }
      if (sortOrder === 'desc') {
        updateSearch({ sortBy: field, sortOrder: 'asc', page: undefined });
        return;
      }
      updateSearch({
        sortBy: undefined,
        sortOrder: undefined,
        page: undefined,
      });
    },
    [sortBy, sortOrder, updateSearch]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearch({ page: newPage > 1 ? newPage : undefined });
    },
    [updateSearch]
  );

  // The assignee filter counts as "active" only when the user has moved
  // it off the default (their own tasks) — to 'all' or a teammate.
  const assigneeIsOverridden =
    !scope && urlAssignee !== undefined && urlAssignee !== currentUserId;
  const hasActiveFilters =
    !!urlTitle ||
    (urlPriorities?.length ?? 0) > 0 ||
    (urlProfiles?.length ?? 0) > 0 ||
    (urlFlags?.length ?? 0) > 0 ||
    (urlItineraryIds?.length ?? 0) > 0 ||
    (urlCollectionIds?.length ?? 0) > 0 ||
    assigneeIsOverridden ||
    !!sortBy ||
    page > 1;

  const handleClearAll = useCallback(() => {
    updateSearch({
      title: undefined,
      priorities: undefined,
      profiles: undefined,
      flags: undefined,
      itineraryIds: undefined,
      collectionIds: undefined,
      // Clearing returns the assignee filter to its default (my tasks).
      assignedAccountId: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      page: undefined,
    });
  }, [updateSearch]);

  const offset = (page - 1) * pageSize;

  const queryInput: FindTasksInput = useMemo(
    () => ({
      offset,
      limit: pageSize,
      title: debouncedSearch || undefined,
      priorities: urlPriorities,
      profiles: urlProfiles,
      flags: urlFlags,
      // The drawer's scope.itineraryId locks the filter; otherwise use
      // whatever the user picked in the Filters popover.
      itineraryIds: scope?.itineraryId ? [scope.itineraryId] : urlItineraryIds,
      collectionIds: urlCollectionIds,
      // Admin = the template library (unassigned tasks, no assignee).
      // Otherwise defaults to the current user on the page; 'all' (or
      // scope on the drawer) overrides. See `effectiveAssignee` above.
      unassignedOnly: isAdmin ? true : undefined,
      assignedAccountId: isAdmin ? undefined : effectiveAssignee,
      // Completed tasks are NEVER hidden — matching the legacy table,
      // which keeps them in the list (with a "Completed On" date) rather
      // than dropping them the instant they're checked off. Filtering by
      // completion is an explicit user choice via the URL `completed`
      // param, not an automatic scope side-effect.
      completed:
        typeof search.completed === 'boolean' ? search.completed : undefined,
      sortBy,
      sortOrder,
    }),
    [
      offset,
      pageSize,
      debouncedSearch,
      JSON.stringify(urlPriorities ?? null),
      JSON.stringify(urlProfiles ?? null),
      JSON.stringify(urlFlags ?? null),
      JSON.stringify(urlItineraryIds ?? null),
      JSON.stringify(urlCollectionIds ?? null),
      scope?.itineraryId,
      effectiveAssignee,
      isAdmin,
      search.completed,
      sortBy,
      sortOrder,
    ]
  );

  // On the full page the assignee defaults to the current user, so hold
  // the query until auth resolves — otherwise the first request fires
  // unfiltered (currentUserId still undefined) and the list flashes every
  // org task before snapping to "my tasks". The drawer (scope) and an
  // explicit URL assignee both have a concrete value already, so they
  // fetch immediately.
  // Admin (templates) has no assignee default, so it's ready immediately.
  const assigneeReady =
    isAdmin || !!scope || urlAssignee !== undefined || !!currentUserId;
  const { data, isLoading, isFetching, isError, error } = useListTasks(
    queryInput,
    { enabled: assigneeReady }
  );
  const tasks = (data?.data ?? []) as Task[];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [modal, setModal] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'apply-set' }
    | { kind: 'edit'; taskId: string }
    | { kind: 'delete'; taskId: string; taskTitle: string }
    | { kind: 'snooze'; taskId: string; expireOn: string | null }
  >({ kind: 'closed' });
  const closeModal = () => setModal({ kind: 'closed' });

  return (
    <div className={styles({})}>
      <div className={css.toolbar}>
        <div className={css.toolbarLeft}>
          <Text variant="muted">
            {isLoading ? ' ' : `${total.toLocaleString('en-US')} tasks`}
          </Text>
          <Input
            className={css.searchInput}
            id="task-search"
            leadingIcon={<Search style={{ height: 14, width: 14 }} />}
            name="task-search"
            placeholder="Search by title..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <TasksFiltersPopover
            assignee={assigneeSelectValue}
            assigneeActive={assigneeIsOverridden}
            collectionIds={urlCollectionIds ?? []}
            flags={urlFlags ?? []}
            hasActive={hasActiveFilters}
            itineraryIds={urlItineraryIds ?? []}
            onChange={(patch) => updateSearch({ ...patch, page: undefined })}
            onClearAll={handleClearAll}
            priorities={urlPriorities ?? []}
            profiles={urlProfiles ?? []}
            showAssignee={!scope && !isAdmin}
          />
        </div>
        <div className={css.toolbarRight}>
          {isAdmin ? null : (
            <Button
              icon={<Plus style={{ height: 14, width: 14 }} />}
              onClick={() => setModal({ kind: 'apply-set' })}
              size="sm"
              variant="outline"
            >
              Add From Collection
            </Button>
          )}
          <Button
            icon={<Plus style={{ height: 14, width: 14 }} />}
            onClick={() => setModal({ kind: 'create' })}
            size="sm"
            variant="primary"
          >
            Add Task
          </Button>
        </div>
      </div>

      <TableContainer
        style={isFetching && !isLoading ? { opacity: 0.7 } : undefined}
      >
        <TableRow header>
          {columns.map((col) => (
            <TableCell align={col.align} key={col.id} width={col.width}>
              {col.sortField ? (
                <SortableHeader
                  active={sortBy === col.sortField}
                  direction={
                    sortBy === col.sortField
                      ? sortOrder === 'asc'
                        ? SORT_ORDER.Asc
                        : sortOrder === 'desc'
                          ? SORT_ORDER.Desc
                          : null
                      : null
                  }
                  onClick={() => {
                    if (col.sortField) handleSortToggle(col.sortField);
                  }}
                >
                  {col.label}
                </SortableHeader>
              ) : (
                <Text variant="muted" weight="medium">
                  {col.label}
                </Text>
              )}
            </TableCell>
          ))}
        </TableRow>
        {isLoading &&
          Array.from({ length: SKELETON_ROWS }, (_, i) => `skeleton-${i}`).map(
            (id) => <TableSkeletonRow key={id} columns={SKELETON_COLUMNS} />
          )}
        {isError && (
          <TableRow>
            <TableCell align="center">
              <Text variant="error">
                Failed to load tasks: {error?.message ?? 'unknown error'}
              </Text>
            </TableCell>
          </TableRow>
        )}
        {!isLoading && !isError && tasks.length === 0 && (
          <TableRow>
            <TableCell align="center">
              <Text variant="muted">No tasks found</Text>
            </TableCell>
          </TableRow>
        )}
        {!isLoading &&
          tasks.map((task) => {
            const expClass =
              expireVariant(task.expireOn) === 'past'
                ? css.expirePast
                : expireVariant(task.expireOn) === 'soon'
                  ? css.expireSoon
                  : undefined;
            const itineraryRef = task.itineraryId
              ? (task.itineraryRef ?? task.itineraryId)
              : null;
            const collectionLabel = task.collectionId
              ? (task.collectionTitle ?? '—')
              : '—';
            const expireOnDisplay = getExpireOnDisplay(task);
            return (
              <TableRow key={task.id}>
                <TableCell>
                  {task.description ? (
                    <Tooltip content={task.description}>
                      <span>
                        <Text weight="semibold">{task.title}</Text>
                      </span>
                    </Tooltip>
                  ) : (
                    <Text weight="semibold">{task.title}</Text>
                  )}
                </TableCell>
                <TableCell align="center" width="110px">
                  <TaskPriorityCell priority={task.priority} taskId={task.id} />
                </TableCell>
                <TableCell align="center" width="100px">
                  <Text variant="muted">{capitalize(task.profile)}</Text>
                </TableCell>
                {isAdmin ? null : (
                  <TableCell align="left" width="130px">
                    {task.itineraryId && itineraryRef ? (
                      <Link
                        className={css.itineraryLink}
                        params={{ itineraryId: task.itineraryId }}
                        to="/itineraries/$itineraryId"
                      >
                        {itineraryRef}
                      </Link>
                    ) : (
                      <Text variant="muted">—</Text>
                    )}
                  </TableCell>
                )}
                <TableCell align="left" width="140px">
                  <Text>{collectionLabel}</Text>
                </TableCell>
                <TableCell align="left" width="180px">
                  {expireOnDisplay.kind === 'absolute' ? (
                    <span className={expClass}>
                      <Text>{expireOnDisplay.text}</Text>
                    </span>
                  ) : (
                    <Text variant="muted">{expireOnDisplay.text}</Text>
                  )}
                </TableCell>
                {isAdmin ? null : (
                  <TableCell align="left" width="180px">
                    <Text>{formatLongDate(task.completedOn)}</Text>
                  </TableCell>
                )}
                <TableCell align="right" width="180px">
                  <div className={css.rowActions}>
                    {isAdmin ? null : (
                      <>
                        <TaskFlagToggle
                          current={coerceFlag(task.flag)}
                          taskId={task.id}
                        />
                        <TaskCompleteButton
                          completed={task.completedOn !== null}
                          taskId={task.id}
                        />
                      </>
                    )}
                    <TaskMoreMenu
                      onDelete={() =>
                        setModal({
                          kind: 'delete',
                          taskId: task.id,
                          taskTitle: task.title,
                        })
                      }
                      onEdit={() => setModal({ kind: 'edit', taskId: task.id })}
                      onSnooze={() =>
                        setModal({
                          kind: 'snooze',
                          taskId: task.id,
                          expireOn: task.expireOn,
                        })
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        <TableFooter>
          <div className={css.paginationRow}>
            <div className={css.pageSize}>
              <Text>Results per page</Text>
              <Select
                dropdownPosition="top"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  handlePageChange(1);
                }}
                options={PAGE_SIZE_OPTIONS}
                size="sm"
                value={String(pageSize)}
              />
            </div>
            <div className={css.pageNav}>
              <Text>
                Page {page} of {totalPages}
              </Text>
              <Pagination>
                <PaginationButton
                  aria-label="First page"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(1)}
                >
                  <ChevronsLeft style={{ height: 14, width: 14 }} />
                </PaginationButton>
                <PaginationButton
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                >
                  <ChevronLeft style={{ height: 14, width: 14 }} />
                </PaginationButton>
                <PaginationButton
                  aria-label="Next page"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  <ChevronRight style={{ height: 14, width: 14 }} />
                </PaginationButton>
                <PaginationButton
                  aria-label="Last page"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(totalPages)}
                >
                  <ChevronsRight style={{ height: 14, width: 14 }} />
                </PaginationButton>
              </Pagination>
            </div>
          </div>
        </TableFooter>
      </TableContainer>

      <TaskCreateModal
        onClose={closeModal}
        open={modal.kind === 'create'}
        variant={isAdmin ? 'admin' : 'default'}
      />
      <TaskUpdateModal
        onClose={closeModal}
        open={modal.kind === 'edit'}
        taskId={modal.kind === 'edit' ? modal.taskId : undefined}
      />
      <ConfirmDeleteDialog
        onClose={closeModal}
        open={modal.kind === 'delete'}
        taskId={modal.kind === 'delete' ? modal.taskId : undefined}
        taskTitle={modal.kind === 'delete' ? modal.taskTitle : undefined}
      />
      <TaskSnoozeModal
        currentSnoozedUntil={
          modal.kind === 'snooze' ? modal.expireOn : undefined
        }
        onClose={closeModal}
        open={modal.kind === 'snooze'}
        taskId={modal.kind === 'snooze' ? modal.taskId : undefined}
      />
      <ApplyCollectionDialog
        collectionId=""
        itineraryId={scope?.itineraryId}
        onClose={closeModal}
        open={modal.kind === 'apply-set'}
      />
    </div>
  );
};
